<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useOnixFlowsStore } from 'dashboard/store/onix/flows';
import OnixFlowsAPI from 'dashboard/api/onix/flows';
import OnixPageLayout from 'dashboard/components-next/onix/OnixPageLayout.vue';
import OnixEmptyState from 'dashboard/components-next/onix/OnixEmptyState.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const route = useRoute();
const flowsStore = useOnixFlowsStore();

const flowId = ref(route.query.flowId ? String(route.query.flowId) : '');
const draft = ref('');
const loading = ref(false);
const messages = ref([]);
const session = ref(null);
const listRef = ref(null);

const flows = computed(() => flowsStore.getFlows);
const fetching = computed(() => flowsStore.uiFlags.fetchingList);

const fieldClass =
  'h-10 px-3 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak';

const stepPhrase = key => {
  const value = String(key || '');
  if (value === 'respond' || value.startsWith('respond')) {
    return t('ONIX.TEST.STEP_REPLY');
  }
  if (value === 'handoff' || value.startsWith('handoff')) {
    return t('ONIX.TEST.STEP_PERSON');
  }
  if (value.startsWith('classify')) return t('ONIX.TEST.STEP_UNDERSTAND');
  if (value.startsWith('instruct')) return t('ONIX.TEST.STEP_GUIDE');
  if (value.startsWith('retrieve')) return t('ONIX.TEST.STEP_SEARCH');
  if (value.startsWith('agent')) return t('ONIX.TEST.STEP_ANSWER');
  if (value.startsWith('condition')) return t('ONIX.TEST.STEP_DECIDE');
  if (value.startsWith('extract')) return t('ONIX.TEST.STEP_DETECT');
  if (value.startsWith('compute')) return t('ONIX.TEST.STEP_CALCULATE');
  if (value.startsWith('actibid')) return t('ONIX.TEST.STEP_INVENTORY');
  return t('ONIX.TEST.STEP_CONTINUE');
};

const stepLine = visit => {
  const phrase = stepPhrase(visit.node_key);
  const outcome = String(visit.outcome || '');
  if (
    outcome === 'handoff' &&
    !String(visit.node_key || '').startsWith('handoff')
  ) {
    return `${phrase}. ${t('ONIX.TEST.THEN_PERSON')}`;
  }
  if (outcome === 'resolve') return `${phrase}. ${t('ONIX.TEST.THEN_CLOSE')}`;
  return phrase;
};

const priorityName = value => {
  const key = String(value || '').toLowerCase();
  if (key === 'low') return t('ONIX.EFFECTS.PRIORITY_LOW');
  if (key === 'medium') return t('ONIX.EFFECTS.PRIORITY_MEDIUM');
  if (key === 'high') return t('ONIX.EFFECTS.PRIORITY_HIGH');
  if (key === 'urgent') return t('ONIX.EFFECTS.PRIORITY_URGENT');
  return '';
};

const notesFor = data => {
  const notes = [];
  if (data?.handoff) notes.push(t('ONIX.TEST.HANDOFF'));
  const details = data?.crm || {};
  if (details.resolve) notes.push(t('ONIX.TEST.RESOLVE'));
  const labels = details.labels || [];
  if (labels.length) {
    notes.push(`${t('ONIX.TEST.LABELS')}: ${labels.join(', ')}`);
  }
  if (details.team_name) {
    notes.push(`${t('ONIX.TEST.TEAM')}: ${details.team_name}`);
  }
  const priority = priorityName(details.priority);
  if (priority) notes.push(`${t('ONIX.TEST.PRIORITY')}: ${priority}`);
  return notes;
};

const scrollToEnd = async () => {
  await nextTick();
  if (listRef.value) listRef.value.scrollTop = listRef.value.scrollHeight;
};

const resetConversation = () => {
  messages.value = [];
  session.value = null;
  draft.value = '';
};

watch(flowId, () => {
  resetConversation();
});

watch(() => messages.value.length, scrollToEnd);

const send = async () => {
  const sent = draft.value.trim();
  if (!flowId.value || !sent || loading.value) return;
  messages.value.push({ sender: 'user', content: sent });
  draft.value = '';
  loading.value = true;
  try {
    const { data } = await OnixFlowsAPI.playground(flowId.value, {
      message_content: sent,
      session: session.value,
    });
    session.value = {
      current_node_key: data?.current_node_key,
      variables: data?.variables || {},
    };
    messages.value.push({
      sender: 'assistant',
      content: data?.response || data?.message || '',
      notes: notesFor(data),
      steps: (data?.node_history || []).map(stepLine),
    });
  } catch {
    messages.value.push({
      sender: 'assistant',
      content: t('ONIX.TEST.ERROR'),
      notes: [],
      steps: [],
    });
  } finally {
    loading.value = false;
  }
};

const onEnter = event => {
  if (event.isComposing || event.shiftKey) return;
  event.preventDefault();
  send();
};

onMounted(() => {
  flowsStore.fetch();
});
</script>

<template>
  <OnixPageLayout
    fill
    :header-title="t('ONIX.TEST.TITLE')"
    :description="t('ONIX.TEST.DESCRIPTION')"
    :is-fetching="fetching"
    :is-empty="!fetching && !flows.length"
  >
    <template #emptyState>
      <OnixEmptyState
        :title="t('ONIX.TEST.EMPTY_TITLE')"
        :subtitle="t('ONIX.TEST.EMPTY_SUBTITLE')"
        :show-backdrop="false"
      />
    </template>
    <template #body>
      <div
        class="flex flex-col h-full min-h-0 rounded-xl border border-n-weak bg-n-solid-1"
      >
        <div
          class="flex flex-wrap items-center justify-between gap-3 px-6 py-4"
        >
          <label class="flex flex-col gap-1 text-sm text-n-slate-12 min-w-0">
            {{ t('ONIX.TEST.FLOW') }}
            <select v-model="flowId" :class="fieldClass" class="min-w-56">
              <option value="">{{ t('ONIX.TEST.FLOW_PLACEHOLDER') }}</option>
              <option
                v-for="flow in flows"
                :key="flow.id"
                :value="String(flow.id)"
              >
                {{ flow.name }}
              </option>
            </select>
          </label>
          <Button
            v-if="messages.length"
            :label="t('ONIX.TEST.RESET')"
            color="slate"
            variant="faded"
            size="sm"
            icon="i-lucide-rotate-ccw"
            @click="resetConversation"
          />
        </div>

        <div ref="listRef" class="flex-1 overflow-y-auto px-6 pb-4 space-y-4">
          <div
            v-for="(message, index) in messages"
            :key="index"
            class="flex"
            :class="message.sender === 'user' ? 'justify-end' : 'justify-start'"
          >
            <div
              class="flex items-end gap-2 max-w-[85%]"
              :class="
                message.sender === 'user' ? 'flex-row-reverse' : 'flex-row'
              "
            >
              <Avatar
                :name="
                  message.sender === 'user'
                    ? t('ONIX.TEST.YOU')
                    : t('ONIX.TEST.ASSISTANT')
                "
                rounded-full
                :size="24"
                class="shrink-0"
              />
              <div class="flex flex-col gap-2 min-w-0">
                <div
                  class="px-4 py-3 text-sm whitespace-pre-wrap text-n-slate-12"
                  :class="
                    message.sender === 'user'
                      ? 'bg-n-solid-blue rounded-br-sm rounded-bl-xl rounded-t-xl'
                      : 'bg-n-solid-iris rounded-bl-sm rounded-br-xl rounded-t-xl'
                  "
                >
                  {{ message.content }}
                </div>
                <div
                  v-if="
                    message.sender === 'assistant' &&
                    (message.notes.length || message.steps.length)
                  "
                  class="text-xs text-n-slate-11 flex flex-col gap-1"
                >
                  <p
                    v-for="(note, noteIndex) in message.notes"
                    :key="noteIndex"
                    class="m-0"
                  >
                    {{ note }}
                  </p>
                  <details v-if="message.steps.length">
                    <summary class="cursor-pointer">
                      {{ t('ONIX.TEST.WHAT_HAPPENED') }}
                    </summary>
                    <ol class="mt-1 mb-0 ps-4 flex flex-col gap-1">
                      <li
                        v-for="(step, stepIndex) in message.steps"
                        :key="stepIndex"
                      >
                        {{ step }}
                      </li>
                    </ol>
                  </details>
                </div>
              </div>
            </div>
          </div>
          <p v-if="loading" class="m-0 text-sm text-n-slate-11">
            {{ t('ONIX.TEST.THINKING') }}
          </p>
        </div>

        <div
          class="flex items-center gap-2 mx-6 mb-6 bg-n-background outline outline-1 outline-n-weak rounded-xl p-3"
        >
          <input
            v-model="draft"
            class="flex-1 bg-transparent border-none focus:outline-none text-sm mb-0 text-n-slate-12 placeholder:text-n-slate-10"
            :placeholder="t('ONIX.TEST.PLACEHOLDER')"
            @keydown.enter.exact="onEnter"
          />
          <Button
            icon="i-lucide-send"
            size="sm"
            variant="ghost"
            :disabled="!draft.trim() || !flowId || loading"
            :is-loading="loading"
            @click="send"
          />
        </div>
      </div>
    </template>
  </OnixPageLayout>
</template>
